import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Food } from "@corpuscomplete/core/nutrition/food";
import { FoodUI } from "@corpuscomplete/core-ui/nutrition/food";
import { Button } from "@corpuscomplete/web-ui/components/button";
import { ItemTitle } from "@corpuscomplete/web-ui/components/item";
import { Text } from "@corpuscomplete/web-ui/components/text";
import { cn } from "@corpuscomplete/web-ui/lib/utils";
import { NutrientAmountsLabel } from "./nutrients-label";
import { FoodItem } from "./food-item";

export function FoodItemGroup({
  label,
  items,
  variant = "full",
  expanded,
  defaultExpanded,
  onExpandedChange,
  renderActions,
  renderContent,
  className,
}: FoodUI.FoodItemGroupProps) {
  const canExpand = items.length > 0;
  const [internalExpanded, setInternalExpanded] = useState(
    defaultExpanded ?? (variant === "summary" ? false : true),
  );
  const isExpanded = expanded ?? internalExpanded;
  const actions = renderActions?.({ expanded: isExpanded });

  const setExpanded = (nextExpanded: boolean) => {
    if (!canExpand || nextExpanded === isExpanded) {
      return;
    }
    if (expanded === undefined) {
      setInternalExpanded(nextExpanded);
    }
    onExpandedChange?.(nextExpanded);
  };

  return (
    <div className={cn("flex min-w-0 flex-col", className)}>
      <div
        className={cn(
          "flex min-w-0 items-start gap-2.5 rounded-lg border-transparent bg-transparent px-2 py-1.5 shadow-none transition-colors [&:hover:not(:has(button:hover))]:bg-muted/40",
        )}
        onClick={() => setExpanded(!isExpanded)}
        onKeyDown={(event) => {
          if (!canExpand) {
            return;
          }
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setExpanded(!isExpanded);
          }
        }}
        role={canExpand ? "button" : undefined}
        tabIndex={canExpand ? 0 : undefined}
        aria-expanded={canExpand ? isExpanded : undefined}
      >
        <div className="min-w-0 flex-1">
          <ItemTitle className="w-full min-w-0 line-clamp-1">{label}</ItemTitle>
          <FoodItemGroupSummary items={items} />
        </div>
        {(actions || canExpand) && (
          <div
            className="flex shrink-0 items-center gap-1 pl-2"
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
          >
            {actions}
            {canExpand && (
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                className="text-muted-foreground hover:bg-muted hover:text-foreground"
                onClick={() => setExpanded(!isExpanded)}
                aria-expanded={isExpanded}
                aria-label={`${isExpanded ? "Collapse" : "Expand"} ${label}`}
              >
                {isExpanded ? <ChevronDown className="size-4" /> : <ChevronUp className="size-4" />}
              </Button>
            )}
          </div>
        )}
      </div>
      {canExpand && isExpanded && (
        <div className="flex min-w-0 flex-col gap-0">
          {renderContent ? (
            renderContent({ expanded: isExpanded })
          ) : (
            <FoodItemGroupRows items={items} />
          )}
        </div>
      )}
    </div>
  );
}

function FoodItemGroupSummary({ items }: { items: readonly Food.Item[] }) {
  const singleItem = items[0] && items.length === 1 ? Food.formatItem(items[0]).subtitle : null;

  return (
    <div className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-muted-foreground">
      <Text variant="xs" className="line-clamp-1 text-muted-foreground">
        {singleItem ?? `${items.length} items`}
      </Text>
      <FoodItemGroupSeparator />
      <NutrientAmountsLabel items={[...items]} abbreviated className="min-w-0 gap-1.5" />
    </div>
  );
}

function FoodItemGroupRows({ items }: { items: readonly Food.Item[] }) {
  return items.map((item, index) => (
    <FoodItem
      key={`${item.food.id}:${item.serving.id}:${item.amount}:${index}`}
      item={item}
      className="rounded-none border-transparent bg-transparent px-0 py-1 shadow-none"
    />
  ));
}

function FoodItemGroupSeparator() {
  return (
    <Text variant="xs" className="text-muted-foreground">
      •
    </Text>
  );
}
